import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EMPTYSTATE — "No extensions yet." Follows the five rules in `Button.tsx`.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── `level` EXISTS BECAUSE A HARDCODED `<h2>` BREAKS THE DOCUMENT OUTLINE ──
 * An empty state is not a page; it is a hole in one. The same component fills a whole route
 * ("No call history"), a card inside a dashboard section that already has its own `<h2>`, and a
 * `<tbody>` under a table that is itself under an `<h3>`. Hardcoding `<h2>` makes exactly one of
 * those correct and the other two produce a heading sequence that jumps or repeats — and heading
 * order is not cosmetic: it is the primary navigation structure for screen-reader users, who
 * move by heading level far more than they read linearly. WCAG 1.3.1 is the clause; the lived
 * symptom is a rotor listing two "level 2" entries where one is plainly a subsection of the
 * other.
 *
 * It is still a REAL `<h2>`…`<h6>` and never a styled `<p role="heading" aria-level>`. The
 * native element is announced by every screen reader in every mode; the ARIA equivalent is a
 * correct-on-paper construction that gains nothing and loses the `<h*>` element that
 * find-in-page, reader modes and browser extensions key off.
 *
 * ── WHY THE ROOT IS A PLAIN `<div>` AND NOT A NAMED `<section>` ────────────
 * A `<section>` with `aria-labelledby` pointing at the heading is a region landmark, which
 * sounds like an upgrade and is not: it puts an entry in the landmark rotor for a state that is,
 * by definition, temporary and about to disappear. The heading is already the navigation handle
 * and it is already in the outline. If a particular empty state really is a top-level region,
 * the page's layout says so around it.
 *
 * ── AND IT IS NOT A LIVE REGION EITHER ────────────────────────────────────
 * "No results match your filter" appearing after a search is genuinely worth announcing, but
 * `role="status"` here would not do it: a live region that is INSERTED already containing its
 * text is frequently never announced, because assistive technology watches registered regions
 * for mutations and a node that arrives complete had no mutation to observe. (`Field.tsx`
 * documents the same trap for validation messages.) The announcement belongs to the persistent
 * container that swaps results for this component — mount the region, change the contents — and
 * `Skeleton.tsx`'s `SkeletonRegion` is that container for the loading half of the same cycle.
 */

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The short statement of what is not here. It is a real heading, so write it as one: "No
   * extensions yet", not "Nothing to see".
   */
  heading: ReactNode;
  /**
   * Heading level. Default 2. Set it to whatever sits one below the heading that encloses this
   * — see the header; the default is right only for a top-level pane.
   */
  level?: HeadingLevel;
  /** Decorative only — `aria-hidden`, like `Button`'s icons. Never put meaning here. */
  icon?: ReactNode;
  /** Why it is empty and what would change that. Optional; the heading has to stand alone. */
  description?: ReactNode;
  /** The way out: usually a `Button`. Rendered after the description so it is the last stop. */
  action?: ReactNode;
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { heading, level = 2, icon, description, action, className, ...rest },
  ref,
) {
  /**
   * `h1`…`h6` as an element name rather than a lookup table of six components. The cast is to
   * the template-literal type, not to `any` — `level` is a closed union, so the result is one of
   * exactly six intrinsic tags and a seventh is a compile error rather than a `<h7>` in the DOM.
   */
  const Heading = `h${level}` as `h${HeadingLevel}`;

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center gap-2 px-6 py-12 text-center',
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span aria-hidden="true" className="mb-1 text-fg-subtle">
          {icon}
        </span>
      ) : null}

      <Heading className="text-base font-semibold text-fg">{heading}</Heading>

      {description ? (
        /**
         * `max-w-prose` and not a pixel width. WCAG 1.4.8 asks for a line length no greater than
         * 80 characters, and `prose` is defined in `ch` units so it stays 80 characters wide
         * when the user scales the text rather than becoming 160 narrow ones.
         */
        <p className="max-w-prose text-sm text-fg-muted">{description}</p>
      ) : null}

      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
});
